import React, { useContext, useEffect, useState } from 'react'
import { Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { GlobalContext } from './context/GlobalContext'
import Sidebar from './Sidebar'
import AppHeader from './components/AppHeader'
import Loading from './Loading'
import Login from './Login'
import Dashboard from './pages/Dashboard'
import Sales from './pages/Sales'
import Customers from './pages/Customers'
import Delivery from './pages/Delivery'
import Reports from './pages/Reports'
import WorkshopEssen from './pages/WorkshopEssen'
import RepVisits from './pages/RepVisits'
import RepVisitLogs from './pages/RepVisitLogs'
import MyHolidays from './pages/MyHolidays'
import Messenger from './pages/Messenger'
import MyVCP from './pages/MyVCP'
import MyVCPManagement from './pages/MyVCPManagement'

// Advanced Reports
import BranchReport from './components/AdvancedReports/reports/BranchReport'
import AdvisorReport from './components/AdvancedReports/reports/AdvisorReport'
import BDMReport from './components/AdvancedReports/reports/BDMReport'
import CustomerAnalysis from './components/AdvancedReports/reports/CustomerAnalysis'
import CustomerContactsReport from './components/AdvancedReports/reports/CustomerContactsReport'
import SalesAnalysis from './components/AdvancedReports/reports/SalesAnalysis'
import AreaComparisonReport from './components/AdvancedReports/reports/AreaComparisonReport'

// Settings pages
import SettingsBranch from './pages/settings/Branch'
import BranchDetail from './pages/BranchDetail'
import SettingsStaff from './pages/settings/Staff'
import SettingsMessages from './pages/settings/Messages'
import SettingsShifts from './pages/settings/Shifts'
import SettingsHolidays from './pages/settings/Holidays'
import SettingsAbout from './pages/settings/About'
import AssignCust from './pages/settings/AssignCust'
import UserManagement from './components/UserManagement/UserManagement'

// TMA pages
import TmaCalendar from './pages/tma/calendar/Calendar'
import TmaShifts from './pages/tma/Shifts'
import TmaHolidays from './pages/tma/Holidays'
import Vehicles from './pages/Vehicles'
import AddVehicle from './pages/AddVehicle';
import VehiclePage from './pages/VehiclePage'
import DriversPage from './pages/DriversPage'
import Incidents from './pages/Incidents'
import FuelLogs from './pages/FuelLogs'


function MainApp({ onLogout }) {
  const { uinfo } = useContext(GlobalContext);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleSidebarCollapse = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  // Create a FleetRedirect component to handle fleet user redirection
  const FleetRedirect = () => {
    const navigate = useNavigate();
    useEffect(() => {
      if (uinfo?.Status === 'fleet') {
        navigate('/fleet/vehicles');
      }
    }, [uinfo, navigate]);
    return null;
  };

  // If on login page, render only the login component
  if (isLoginPage) {
    return <Login />;
  }

  return (
    <div className="h-screen flex flex-col">
      <AppHeader sidebarCollapsed={sidebarCollapsed} />
      <div className="flex overflow-hidden flex-1">
        <Sidebar onLogout={onLogout} onCollapseChange={handleSidebarCollapse} />
        <div className="overflow-y-auto overflow-x-hidden relative flex-1 p-6 bg-gray-100 pt-20">
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route path="/" element={
            <>
              <FleetRedirect />
              <Dashboard />
            </>
          } />
          {/* Temporary route to view old Dashboard with tables */}
          <Route path="/dashboard-old" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <Dashboard />
          } />
          <Route path="/messenger" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <Messenger />
          } />
          <Route path="/my-vcp" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <MyVCP />
          } />
          <Route path="/my-vcp/management" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <MyVCPManagement />
          } />
          <Route path="/sales" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <Sales />
          } />
          <Route path="/customers" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <Customers />
          } />
          <Route path="/repvisits" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <RepVisits />
          } />
          <Route path="/repvisits/logs" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <RepVisitLogs />
          } />
          <Route path="/workshopessen" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <WorkshopEssen />
          } />
          <Route path="/delivery" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <Delivery />
          } />
          <Route path="/reports" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <Reports />
          } />
          
          {/* Advanced Reports Routes */}
          <Route path="/reports/branch" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <BranchReport />
          } />
          <Route path="/reports/advisor" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <AdvisorReport />
          } />
          <Route path="/reports/bdm" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <BDMReport />
          } />
          <Route path="/reports/customer-analysis" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <CustomerAnalysis />
          } />
          <Route path="/reports/customer-contacts" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <CustomerContactsReport />
          } />
          <Route path="/reports/sales-analysis" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SalesAnalysis />
          } />
          <Route path="/reports/area-comparison" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <AreaComparisonReport />
          } />
          <Route path="/myholidays" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <MyHolidays />
          } />
          <Route path="/fleet/vehicles" element={<Vehicles />} />
          <Route path="/fleet/vehicles/add" element={<AddVehicle />} />
          <Route path="/fleet/vehicles/:id" element={<VehiclePage />} />
          <Route path="/fleet/drivers" element={<DriversPage />} />
          <Route path="/fleet/incidents" element={<Incidents />} />
          <Route path="/fleet/fuel" element={<FuelLogs />} />



          {/* Settings Routes */}
          <Route path="/settings/branch" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SettingsBranch />
          } />
          <Route path="/branch/:branchCode" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <BranchDetail />
          } />
          <Route path="/settings/staff" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SettingsStaff />
          } />
          <Route path="/settings/assigncust" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <AssignCust />
          } />
          <Route path="/settings/messages" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SettingsMessages />
          } />
          <Route path="/settings/shifts" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SettingsShifts />
          } />
          <Route path="/settings/holidays" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SettingsHolidays />
          } />
          <Route path="/settings/about" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <SettingsAbout />
          } />
          <Route path="/settings/users" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : 
            uinfo?.Status === 'admin' ? <UserManagement /> : 
            <Navigate to="/" replace />
          } />

          <Route path="/tma/calendar" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <TmaCalendar />
          } />
          <Route path="/tma/holidays" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <TmaHolidays />
          } />
          <Route path="/tma/shifts" element={
            uinfo?.Status === 'fleet' ? <FleetRedirect /> : <TmaShifts />
          } />
        </Routes>
        </div>
      </div>
    </div>
  )
}

export default MainApp
